import React, { useEffect, useState } from "react";
import { TbArrowRight } from "react-icons/tb";

interface ProjectProps {
  name: string;
  description: string;
  award?: {
    icon: React.ReactNode;
    title: string;
    description: string;
    link: string;
  };
  awardImage?: any;
  downloadTitle?: string;
  downloadLink?: string;
  homepageLink?: string;
  imageOne: any;
  imageTwo?: any;
  backgroundColor: string;
}

const Project: React.FC<ProjectProps> = (props) => {
  const {
    name,
    description,
    award,
    awardImage,
    downloadTitle,
    downloadLink,
    homepageLink,
    imageOne,
    imageTwo,
    backgroundColor
  } = props;

  const [isAwardModalOpen, setIsAwardModalOpen] = useState(false);

  useEffect(() => {
    if (!isAwardModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsAwardModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isAwardModalOpen]);

  const openAwardModal = () => {
    if (!awardImage) return;
    setIsAwardModalOpen(true);
  };

  return (
    <div className="pb-16 sm:pb-24">
      <div
        className="flex flex-col items-center text-black text-center rounded-3xl sm:rounded-[60px] overflow-hidden"
        style={{ backgroundColor: backgroundColor }}
      >
        {imageTwo ? (
          <div className="w-full px-4 sm:px-8 h-[420px] sm:h-[520px] lg:h-[600px]">
            <div className="flex h-full flex-col sm:flex-row items-center sm:items-stretch justify-center gap-6 sm:gap-10 lg:gap-24">
              <div className="flex w-full sm:w-auto flex-1 items-end justify-center">
                <img
                  src={imageOne}
                  alt="Project screenshot"
                  className="w-full max-w-[320px] sm:max-w-[300px] md:max-w-[340px] lg:max-w-[375px] max-h-full object-contain"
                />
              </div>
              <div className="flex w-full sm:w-auto flex-1 items-start justify-center">
                <img
                  src={imageTwo}
                  alt="Project screenshot"
                  className="w-full max-w-[320px] sm:max-w-[300px] md:max-w-[340px] lg:max-w-[375px] max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full px-4 sm:px-8 h-[420px] sm:h-[520px] lg:h-[600px]">
            <div className="flex h-full items-end justify-center">
              <img
                src={imageOne}
                alt="Project screenshot"
                className="w-full max-w-[892px] max-h-full object-contain"
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 w-full">
        <div className="flex flex-col items-start">
          <h2 className="font-sans font-semibold text-xl sm:text-2xl text-black">
            {name}
          </h2>
          <p className="font-sans text-base sm:text-lg text-black">
            {description}
          </p>
        </div>

        {award && (
          <div className="flex lg:flex-1 lg:justify-center">
            {award.link ? (
              <a
                href={award.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-white text-black font-sans font-semibold py-2 px-2 rounded-full flex items-center justify-start border border-black border-opacity-10"
              >
                {award.icon && (
                  <span
                    className="mr-2 text-xl sm:text-2xl bg-maroBackground rounded-full flex items-center justify-center shrink-0"
                    style={{ width: "40px", height: "40px" }}
                  >
                    {award.icon}
                  </span>
                )}
                <div className="min-w-0">
                  <h3 className="font-sans font-semibold text-[15px] sm:text-[16px] text-left leading-tight truncate">
                    {award.title}
                  </h3>
                  <p className="font-sans font-normal text-[13px] sm:text-[14px] text-left text-maroTextSecondary leading-tight truncate">
                    {award.description}
                  </p>
                </div>
                <TbArrowRight
                  strokeWidth={2.5}
                  className="ml-2 mr-1 text-lg shrink-0"
                />
              </a>
            ) : (
              <button
                type="button"
                onClick={openAwardModal}
                className="w-full sm:w-auto bg-white text-black font-sans font-semibold py-2 px-2 rounded-full flex items-center justify-start border border-black border-opacity-10"
              >
                {award.icon && (
                  <span
                    className="mr-2 text-xl sm:text-2xl bg-maroBackground rounded-full flex items-center justify-center shrink-0"
                    style={{ width: "40px", height: "40px" }}
                  >
                    {award.icon}
                  </span>
                )}
                <div className="min-w-0">
                  <h3 className="font-sans font-semibold text-[15px] sm:text-[16px] text-left leading-tight truncate">
                    {award.title}
                  </h3>
                  <p className="font-sans font-normal text-[13px] sm:text-[14px] text-left text-maroTextSecondary leading-tight truncate">
                    {award.description}
                  </p>
                </div>
                <TbArrowRight
                  strokeWidth={2.5}
                  className="ml-2 mr-1 text-lg shrink-0"
                />
              </button>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-stretch sm:items-center">
          {homepageLink && (
            <a
              href={homepageLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-white text-black font-sans font-semibold py-3 sm:py-4 px-5 sm:px-6 rounded-full flex items-center justify-center border border-black border-opacity-10"
            >
              See details{" "}
              <TbArrowRight strokeWidth={2.5} className="ml-1 text-lg" />
            </a>
          )}
          {downloadLink && (
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-black text-white font-sans font-semibold py-3 sm:py-4 px-5 sm:px-6 rounded-full flex items-center justify-center"
            >
              {downloadTitle}{" "}
              <TbArrowRight strokeWidth={2.5} className="ml-1 text-lg" />
            </a>
          )}
        </div>
      </div>

      {awardImage && isAwardModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${name} award image`}
          onClick={() => setIsAwardModalOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />
          <div
            className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={awardImage}
              alt={`${name} feature`}
              className="h-full w-full object-contain"
            />
            <button
              type="button"
              onClick={() => setIsAwardModalOpen(false)}
              className="absolute right-3 top-3 rounded-full bg-black/70 px-3 py-1 font-sans text-sm font-semibold text-white"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Project;
